import {exportToCSV, exportToPDF} from '../../../../utils/exportUtils';
import React, {useRef, useState} from 'react';
import AppCard from '../../../../components/ui/AppCard';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import {InputText} from 'primereact/inputtext';
import {Toast} from 'primereact/toast';
import apiClient from "../../../../services/api-client";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import StorageHelper from '../../../../utils/storage-helper';

const DeviceReportsTable = () => {
    const [payments, setPayments] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => null);
    const [toDate, setToDate] = useState(() => null);
    const [serialNumber, setSerialNumber] = useState('');

    const dt = useRef(null);
    const toast = useRef(null);

    const fetchDeviceReport = async () => {
        if (!serialNumber || serialNumber.trim() === '') {
            toast.current.show({
                severity: 'warn',
                summary: 'Missing Serial Number',
                detail: 'Please enter a serial number',
                life: 3000
            });
            return;
        }

        const effectiveFromDate = fromDate ?? new Date();
        const effectiveToDate = toDate ?? new Date();

        setLoading(true);
        try {
            const formattedFromDate = effectiveFromDate.toISOString().split('T')[0];
            const formattedToDate = effectiveToDate.toISOString().split('T')[0];

            const response = await apiClient.get(`report/device`,
                {
                    params: {
                        serialNumber: serialNumber.trim(),
                        from: formattedFromDate,
                        to: formattedToDate
                    },
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token')
                    }
                }
            );

            if (response.data.status === "SUCCESS") {
                setPayments(response.data.responseBody);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Device report loaded successfully',
                    life: 3000
                });
            } else {
                console.error('Failed to fetch device report:', response.data.status);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch device report',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error fetching device report:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch device report. Please try again.',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({selectionOnly, filename: serialNumber || "device-report"});
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const paymentStatusBodyTemplate = (rowData) => {
        const status = rowData.paymentStatus;
        if (!status) return <span
            className="badge border-0 font-semibold text-xs bg-base-200/50 text-base-content">-</span>;
        const upperStatus = status.toUpperCase();
        let bg = 'bg-base-200/50', text = 'text-base-content';
        if (upperStatus === 'PENDING') {
            bg = 'bg-warning/10';
            text = 'text-warning';
        } else if (upperStatus === 'SUCCESS' || upperStatus === 'COMPLETED' || upperStatus === 'SUCCESSFUL' || upperStatus.includes('NMB') || upperStatus.includes('CR')) {
            bg = 'bg-success/10';
            text = 'text-success';
        } else if (upperStatus === 'FAILED' || upperStatus === 'DECLINED') {
            bg = 'bg-error/10';
            text = 'text-error';
        } else if (upperStatus === 'PROCESSING') {
            bg = 'bg-info/10';
            text = 'text-info';
        }
        return <span className={`badge border-0 font-semibold text-xs ${bg} ${text}`}>{status}</span>;
    };

    const paymentMethodBodyTemplate = (rowData) => {
        const method = rowData.paymentMethod;
        if (!method) return null;
        return <span className="badge badge-outline font-semibold text-xs badge-ghost uppercase">{method}</span>;
    };

    return (
        <div className="w-full">
            <Toast ref={toast}/>
            <AppCard title="POS Device Report">

                <div
                    className="flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center mb-6 bg-base-100/50 p-4 rounded-xl border border-base-200">
                    <div className="join w-full xl:w-auto">
                        <InputText
                            id="serialNumber"
                            value={serialNumber}
                            onChange={(e) => setSerialNumber(e.target.value)}
                            placeholder="Device Serial No."
                            className="input input-bordered join-item h-10 w-full md:w-48"
                        />
                        <Calendar
                            id="fromDate"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.value)}
                            dateFormat="yy-mm-dd"
                            placeholder="From Date"
                            inputClassName="input input-bordered join-item h-10 w-full md:w-36"
                            className="join-item"
                        />
                        <Calendar
                            id="toDate"
                            value={toDate}
                            onChange={(e) => setToDate(e.value)}
                            dateFormat="yy-mm-dd"
                            placeholder="To Date"
                            inputClassName="input input-bordered join-item h-10 w-full md:w-36"
                            className="join-item"
                        />
                        <button
                            className="btn btn-primary join-item text-white font-semibold"
                            onClick={fetchDeviceReport}
                            disabled={loading}
                        >
                            {loading ? <span className="loading loading-spinner loading-sm"></span> :
                                <i className="pi pi-sync"></i>}
                            Generate
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto ml-auto">
                        <div className="relative w-full sm:w-64">
                            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
                            <input
                                type="text"
                                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                                placeholder="Search..."
                                value={globalFilter}
                                onChange={onGlobalFilter}
                            />
                        </div>
                        <div className="dropdown dropdown-end w-full sm:w-auto">
                            <div tabIndex={0} role="button"
                                 className="btn btn-outline w-full sm:w-auto btn-ghost h-10 min-h-0 shadow-sm rounded-full"
                                 disabled={payments.length === 0}>
                                <i className="pi pi-file-export"></i> Export
                            </div>
                            <ul tabIndex={0}
                                className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                <li><a onClick={() => exportToCSV(dt, 'Device Payments')}><i
                                    className="pi pi-file-excel text-success"></i> Download CSV</a></li>
                                <li><a onClick={() => exportToPDF(dt, 'Device Payments', 'Device Payments Report')}><i
                                    className="pi pi-file-pdf text-error"></i> Download PDF</a></li>
                            </ul>
                        </div>
                    </div>
                </div>

                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={payments}
                    loading={loading}
                    globalFilter={globalFilter}
                    exportFilename={serialNumber}
                    emptyMessage="No payment records found. Please enter a serial number, select a date range, and generate report."
                    paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                >
                    <Column field="policyNumber" header="Policy Number" sortable/>
                    <Column field="productName" header="Product Name" sortable/>
                    <Column field="payerName" header="Payer Name" sortable/>
                    <Column field="paymentMethod" header="Payment Method" body={paymentMethodBodyTemplate} sortable/>
                    <Column field="amount" header="Amount" body={amountBodyTemplate} sortable/>
                    <Column field="paymentStatus" header="Payment Status" body={paymentStatusBodyTemplate}
                            sortable/>
                    <Column field="timeStamp" header="Timestamp"
                            body={(rowData) => dateBodyTemplate(rowData, 'timeStamp')} sortable/>
                </DataTable>
            </AppCard>
        </div>
    );
}

export default DeviceReportsTable;